import React, { useState } from "react";
import {
  FormContainer,
  Form,
  Input,
  Label,
  Heading,
  SubmitButton,
  Textarea,
} from "./OfferForm.styles";
import { toast } from "react-toastify";
import LoadingPage from "../../components/shared-components/Loading/Loading";
import { useNavigate } from "react-router-dom";

function ProjectForm() {
  const [formData, setFormData] = useState({
    fullName: "",
    companyName: "",
    email: "",
    phoneNumber: "",
    projectTypes: [],
    projectDescription: "",
    preferredStartDate: "",
    budget: "",
    heardFrom: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();

  const projectTypeOptions = [
    "Process Automation",
    "Predictive Analytics",
    "Chatbot / Virtual Assistant",
    "Data Integration",
    "Custom AI Solution",
  ];

  const budgetOptions = [
    "₦500k - ₦1.9 million",
    "₦2 million - ₦4.9 million",
    "₦5 million - ₦9.9 million",
    "Above ₦10 million",
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        projectTypes: checked
          ? [...prev.projectTypes, value]
          : prev.projectTypes.filter((item) => item !== value),
      }));
      setErrors((prevErrors) => ({
        ...prevErrors,
        projectTypes: "",
      }));
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim())
      newErrors.fullName = "Full name is required.";
    if (!formData.companyName.trim())
      newErrors.companyName = "Company name is required.";
    if (!formData.email.trim()) newErrors.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Invalid email format.";
    if (!formData.phoneNumber.trim())
      newErrors.phoneNumber = "Phone number is required.";
    else if (!/^\d{10,15}$/.test(formData.phoneNumber))
      newErrors.phoneNumber = "Invalid phone number format.";
    if (formData.projectTypes.length === 0)
      newErrors.projectTypes = "At least one project type is required.";
    if (!formData.projectDescription.trim())
      newErrors.projectDescription = "Project description is required.";
    if (!formData.preferredStartDate)
      newErrors.preferredStartDate = "Preferred start date is required.";
    if (!formData.budget) newErrors.budget = "Budget range is required.";
    if (!formData.heardFrom.trim())
      newErrors.heardFrom = "How did you hear about us? is required.";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.warn("Please fill all required fields.");
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-passcode": process.env.REACT_APP_PASSCODE,
        },
        body: JSON.stringify({
          formData,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(
          "Project form submitted successfully! An AI consultant will reach out soon."
        );
        setFormData({
          fullName: "",
          companyName: "",
          email: "",
          phoneNumber: "",
          projectTypes: [],
          projectDescription: "",
          preferredStartDate: "",
          budget: "",
          heardFrom: "",
        });
        navigate("/call");
      } else {
        if (data.errors) {
          Object.values(data.errors).forEach((error) => {
            toast.warn(error);
          });
          setErrors(data.errors);
        } else {
          toast.error(data.error || "Failed to submit project form.");
        }
      }
    } catch (err) {
      toast.error("An error occurred while submitting the form!");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <FormContainer>
      <Heading>Quick Project Form — Let's Get Started</Heading>
      <Form onSubmit={handleSubmit}>
        <div>
          <Label htmlFor="fullName">Full Name</Label>
          <Input
            type="text"
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            placeholder="Enter your full name"
          />
          {errors.fullName && <p style={{ color: "red" }}>{errors.fullName}</p>}
        </div>

        <div>
          <Label htmlFor="companyName">Company Name</Label>
          <Input
            type="text"
            id="companyName"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            placeholder="Enter your company name"
          />
          {errors.companyName && (
            <p style={{ color: "red" }}>{errors.companyName}</p>
          )}
        </div>

        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email"
          />
          {errors.email && <p style={{ color: "red" }}>{errors.email}</p>}
        </div>

        <div>
          <Label htmlFor="phoneNumber">Phone Number</Label>
          <Input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            placeholder="Enter your phone number"
          />
          {errors.phoneNumber && (
            <p style={{ color: "red" }}>{errors.phoneNumber}</p>
          )}
        </div>

        <div>
          <Label>Project Type (select all that apply)</Label>
          {projectTypeOptions.map((option) => (
            <div
              key={option}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "0.5rem",
                alignContent: "center",
                justifyItems: "center",
                alignItems: "center",
              }}
            >
              <Input
                type="checkbox"
                id={option}
                name="projectTypes"
                value={option}
                checked={formData.projectTypes.includes(option)}
                onChange={handleChange}
                style={{
                  cursor: "pointer",
                  width: "1.2rem",

                  height: "1.2rem",
                }}
              />
              <Label
                htmlFor={option}
                style={{
                  cursor: "pointer",
                  marginTop: "0.2rem",
                  whiteSpace: "nowrap", // keeps text in one line
                }}
              >
                {option}
              </Label>
            </div>
          ))}
          {errors.projectTypes && (
            <p style={{ color: "red" }}>{errors.projectTypes}</p>
          )}
        </div>

        <div>
          <Label htmlFor="projectDescription">
            Briefly describe your project or goal
          </Label>
          <Textarea
            id="projectDescription"
            name="projectDescription"
            value={formData.projectDescription}
            onChange={handleChange}
            placeholder="Describe your project or goal..."
          />
          {errors.projectDescription && (
            <p style={{ color: "red" }}>{errors.projectDescription}</p>
          )}
        </div>

        <div>
          <Label htmlFor="preferredStartDate">Preferred Start Date</Label>
          <Input
            type="date"
            id="preferredStartDate"
            name="preferredStartDate"
            value={formData.preferredStartDate}
            onChange={handleChange}
          />
          {errors.preferredStartDate && (
            <p style={{ color: "red" }}>{errors.preferredStartDate}</p>
          )}
        </div>

        <div>
          <Label htmlFor="budget">Project Budget Range</Label>
          <Input
            as="select"
            id="budget"
            name="budget"
            value={formData.budget}
            onChange={handleChange}
            style={{ padding: "0.75rem" }}
          >
            <option value="">Select budget range</option>
            {budgetOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Input>
          {errors.budget && <p style={{ color: "red" }}>{errors.budget}</p>}
        </div>

        <div>
          <Label htmlFor="heardFrom">How did you hear about us?</Label>
          <Input
            type="text"
            id="heardFrom"
            name="heardFrom"
            value={formData.heardFrom}
            onChange={handleChange}
            placeholder="How did you hear about us?"
          />
          {errors.heardFrom && (
            <p style={{ color: "red" }}>{errors.heardFrom}</p>
          )}
        </div>

        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
          }}
        >
          <SubmitButton disabled={isLoading} type="submit">
            Submit
          </SubmitButton>
        </div>
      </Form>
      <p style={{ fontSize: "0.875rem", color: "#666", marginTop: "1rem" }}>
        Once you submit this form, one of our AI consultants will reach out,
        usually within a few hours to discuss your project and next steps.
      </p>
    </FormContainer>
  );
}

export default ProjectForm;
